import {
  Button,
  CircularProgress,
  Dialog,
  Divider,
  Grid,
  Typography,
  useTheme,
  Box,
} from '@mui/material';

import { useEffect, useState } from 'react';
import { getQuotesPdf, refreshQuoteCache } from '@shared/services/services';
import { Document, Page } from '@react-pdf/renderer';
import { useDispatch, useSelector } from 'react-redux';
import { saveQuoteSelected } from '@shared/redux/actions';
import { useHistory } from 'react-router-dom';
import { ASSIST_BUY } from '@shared/helpers/routes';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

import { calculateAge } from '@shared/helpers/calculateAge';

import assistLogoWhite from '@shared/assets/assistLogoWhite.svg';
import CheckIcon from '@mui/icons-material/Check';

import MuiDialogTitle from '@mui/material/DialogTitle';
import MuiDialogContent from '@mui/material/DialogContent';
import MuiDialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import ListItem from '@mui/material/ListItem';

import List from '@mui/material/List';
import LoadingImage from '@shared/components/LoadingImage';
import { getUser } from '@shared/helpers/auth';

import TransactionButton from '@shared/components/TransactionButton';
import { currency } from '@shared/hooks/currencies';

import { sxStyle } from './styles';

function diferenciaEnDias(fechaInicio, fechaFin) {
  // Función auxiliar para convertir una fecha en formato dd/mm/yyyy a un objeto Date
  function parsearFecha(fechaStr) {
    const [dia, mes, anio] = fechaStr.split('/').map(Number);
    // En JavaScript los meses van de 0 a 11
    return new Date(anio, mes - 1, dia);
  }

  const fecha1 = parsearFecha(fechaInicio);
  const fecha2 = parsearFecha(fechaFin);

  // Calcular la diferencia en milisegundos y convertir a días
  const diferenciaMs = fecha2 - fecha1;
  const unDiaMs = 1000 * 60 * 60 * 24;
  const diferenciaDias = diferenciaMs / unDiaMs;

  return Math.round(diferenciaDias) + 1; // Redondear por si hay diferencias horarias
}

export const CardAssist = ({
  element,
  canBuy,
  canEmitToAccount,
  xTransactionId,
}) => {
  const theme = useTheme();

  const storageKey = 'assist_order_time';

  const searchData = useSelector(store => store.searchAssistReducer);
  const history = useHistory();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [quotePdf, setQuotePdf] = useState();

  const [isLoadingResults, setIsLoadingResults] = useState(false);

  const fetchQuotesPdf = async params => {
    try {
      const response = await getQuotesPdf(params);
      setQuotePdf(response.url);
    } catch (error) {
      console.error('error', error);
    }
  };

  const handleBuy = () => {
    refreshQuoteCache(element?.id_transaction);

    searchData.emit = false;
    dispatch(
      saveQuoteSelected({
        ...element,
        xTransactionId: xTransactionId,
        emit: false,
      })
    );
    localStorage.removeItem(storageKey);
    history.push({
      pathname: ASSIST_BUY,
      state: {
        elementSelected: element,
        xTransactionId: xTransactionId,
        datosDeBusqueda: searchData,
        passengers: element.clientesCotizados.clienteCotizacion,
        storageKey,
      },
    });
  };

  const handleEmit = () => {
    if (canEmit()) {
      refreshQuoteCache(element?.id_transaction);
      searchData.emit = true;
      dispatch(
        saveQuoteSelected({
          ...element,
          xTransactionId: xTransactionId,
          emit: true,
        })
      );
      localStorage.removeItem(storageKey);

      history.push({
        pathname: ASSIST_BUY,
        state: {
          elementSelected: element,
          xTransactionId: xTransactionId,
          datosDeBusqueda: searchData,
          passengers: element.clientesCotizados.clienteCotizacion,
          storageKey,
        },
      });
    }
  };

  const handleClickOpen = async () => {
    if (quotePdf) {
      setOpen(true);
    } else {
      try {
        setIsLoadingResults(true);
        const params = {
          product_code: element?.codigo,
          rate_code: element?.codigoTarifa,
          pax_age: calculateAge(
            element?.clientesCotizados?.clienteCotizacion[0]?.fechaNacimiento
          ),
          start_date: element?.start_date,
          end_date: element?.end_date,
          is_annual: element?.is_annual,
        };
        await fetchQuotesPdf(params);
        setIsLoadingResults(false);
        setOpen(true);
      } catch (error) {
        console.error(error);
        setIsLoadingResults(false);
      }
    }
  };

  const handleClickOpenModal = async () => {
    setOpenDetail(true);
  };

  const handleClose = () => {
    setOpen(false);
    setOpenDetail(false);
  };

  useEffect(() => {
    return () => {
      // Liberar el objeto URL cuando el componente se desmonte
      if (quotePdf) {
        window.URL.revokeObjectURL(quotePdf);
      }
    };
  }, [quotePdf]);

  useEffect(() => {}, []);

  const canEmit = () => {
    const user = getUser();
    return !!user?.user?.entity?.can_emit_assist_card;
  };

  function coberturaMedica(element) {
    if (element?.details?.medical_assistance_amount) {
      return (
        <span>
          Asistencia Médica
          <Box component="span" sx={sxStyle.brSpan}>
            <br />
          </Box>{' '}
          USD{' '}
          {element?.details?.medical_assistance_amount.toLocaleString('es-AR', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}
        </span>
      );
    } else {
      return element?.tarifaLeyenda?.split('-')?.[0]?.split('+')?.[0]?.trim();
    }
  }

  let texto = '';

  if (element?.is_annual) {
    texto = '¡Disfruta de cobertura por un año!';
  } else {
    const dias = diferenciaEnDias(
      element?.start_date?.toString(),
      element?.end_date?.toString()
    );

    texto = `Total ${dias} ${dias > 1 ? 'días' : 'día'}`;
  }

  const getProductName3 = elem => {
    return elem?.details?.plan_name ?? elem?.nombreProducto;
  };

  return (
    <Grid
      container
      item
      xs={12}
      sm={12}
      md={6}
      lg={3}
      sx={sxStyle.paddingCards}
    >
      <Grid
        container
        item
        xs={12}
        sx={{ ...sxStyle.root, paddingBottom: '30px' }}
      >
        <LoadingImage site={'AssistCard'} isOpen={isLoadingResults} />
        <Grid container item xs={12}>
          <Grid
            item
            xs={12}
            sx={{
              ...sxStyle.destinationContainer,
              minHeight: '84px',
              position: 'relative', // corregí "realative"
              minWidth: 'calc(100% + 6px)',
              ml: '-3px',
              mt: '-3px',
              backgroundColor: element?.is_annual ? '#000000' : '#f63131',
            }}
          >
            <Box
              component="img"
              src={assistLogoWhite}
              alt="assistLogo"
              sx={{ ...sxStyle.assistLogo, maxHeight: '60px' }}
            />

            {element?.is_annual ? (
              <>
                <Box component="span" sx={sxStyle.miltiviajeHide}>
                  <Box
                    sx={{
                      ...sxStyle.assistProductTitle,
                      top: '16px',
                      right: '38px',
                    }}
                  >
                    Multiviaje
                  </Box>
                  <Box sx={{ ...sxStyle.assistProductTitle, top: '40px' }}>
                    Anual
                  </Box>
                </Box>
                <Box component="span" sx={sxStyle.miltiviajeCenter}>
                  <Box sx={sxStyle.assistProductTitle}>Multiviaje</Box>
                </Box>
              </>
            ) : (
              <Box sx={sxStyle.assistProductTitle}>
                {element?.is_annual ? '' : getProductName3(element)}
              </Box>
            )}

            {element?.is_agreement && (
              <Box
                sx={{
                  ...sxStyle.assistRecomended,
                  backgroundColor: element?.is_annual ? '#f63131' : 'black',
                }}
              >
                ¡Recomendado!
              </Box>
            )}
          </Grid>
        </Grid>
        <Grid
          item
          xs={12}
          sx={{ ...sxStyle.coverageContainer, p: '20px 0px 0px 0px' }}
        >
          <Grid sx={{ ...sxStyle.coverageDataContainer, paddingBottom: '0px' }}>
            <Grid
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography sx={sxStyle.brSpanAux}>
                {coberturaMedica(element)}
              </Typography>
            </Grid>

            <Typography
              onClick={handleClickOpenModal}
              sx={{
                ...sxStyle.coverageDetailButton,
                color: theme.tertiary,
                textAlign: 'center',
                marginBottom: '0px',
                marginTop: '-10px',
                fontSize: '16px',
              }}
            >
              Ver detalle cobertura
            </Typography>

            <Grid
              item
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{
                  ...sxStyle.tarifaLeyenda,
                  fontSize: '26px',
                  fontWeight: 'bold',
                  color: 'red',
                }}
              >
                {element?.is_annual ? (
                  <>
                    <span style={{ fontSize: '20px' }}>
                      {'Cobertura ' + getProductName3(element)}
                    </span>
                    <br />
                    Multiviaje Anual
                  </>
                ) : (
                  <>
                    <span style={{ fontSize: '20px' }}>Precio por día</span>
                    <br />
                    <>
                      {currency(
                        element?.price?.details_USD?.total_usd_night_unrounded,
                        true
                      )}
                    </>
                  </>
                )}
              </Typography>
            </Grid>
            <Grid
              item
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ ...sxStyle.tarifaLeyenda, fontWeight: 'bold' }}>
                {texto}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <Typography sx={{ ...sxStyle.tarifaLeyenda, paddingTop: '0px' }}>
            {element?.is_annual
              ? 'Cobertura anual'
              : 'Desde ' +
                element?.start_date?.toString() +
                ' hasta ' +
                element?.end_date?.toString()}
          </Typography>
        </Grid>
        <Grid
          item
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <Typography sx={{ ...sxStyle.tarifaLeyenda, paddingTop: '0px' }}>
            {element?.clientesCotizados?.clienteCotizacion.length}{' '}
            {element?.clientesCotizados?.clienteCotizacion.length > 1
              ? 'Pasajeros'
              : 'Pasajero'}
          </Typography>
        </Grid>

        <Grid
          item
          xs={12}
          sx={{ ...sxStyle.priceContainer, p: '0px 0px 0px 0px' }}
        >
          <Grid
            sx={{
              ...sxStyle.priceDataContainer,
              paddingBottom: '0px',
              paddingTop: '12px',
              justifyContent: 'center',
            }}
          >
            <Typography sx={sxStyle.priceTotal}>
              {currency(element?.price?.details_USD?.total, true)}
            </Typography>
          </Grid>
          <Grid
            container
            direction="column"
            alignItems="center"
            sx={{ width: '100%' }}
          >
            {/* momentaneamente suspendido */}
            <Grid item sx={{ width: { xs: 'auto', sm: 210 } }}>
              <TransactionButton
                variant="buy"
                disabled={!canBuy}
                onClick={handleBuy}
                width="95%"
              >
                Comprar
              </TransactionButton>
            </Grid>

            {canEmit() ? (
              <Grid item sx={{ width: { xs: 'auto', sm: 210 }, mt: 1, mb: 2 }}>
                <TransactionButton
                  variant="emit"
                  disabled={!canEmitToAccount}
                  onClick={handleEmit}
                  width="95%"
                >
                  Emitir a cuenta
                </TransactionButton>
              </Grid>
            ) : (
              <br />
            )}
          </Grid>
        </Grid>

        <Grid
          item
          xs={12}
          sx={{
            ...sxStyle.destinationContainer,
            position: 'realative',
            backgroundColor: 'rgb(149 149 149)',
            color: 'white',
            fontWeight: 'bold',
            borderRadius: '0px',
            minWidth: 'calc(100% + 6px)',
            marginLeft: '-3px',
          }}
        >
          <div
            style={{
              fontFamily: "'Open Sans', sans-serif",
              fontSize: '16px',
              textAlign: 'center',
            }}
          >
            Cobertura
          </div>
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            ...sxStyle.coverageDataContainer,
            position: 'realative',
            padding: '10px 20px 0px 20px',
            minHeight: '160px',
            maxHeight: '160px',
            overflowY: 'hidden',
          }}
        >
          {element?.details?.assist_plans_coverages
            ?.slice(0, 2)
            .map((el, index) => (
              <Grid
                sx={{ display: 'flex', alignItems: 'center' }}
                key={index + el?.description}
              >
                <CheckIcon sx={{ color: '#28bf54' }}></CheckIcon>
                <Typography
                  sx={{ ...sxStyle.tarifaLeyenda, textAlign: 'left' }}
                >
                  {el.description + ': ' + el.value}
                </Typography>
              </Grid>
            ))}
        </Grid>

        <Dialog
          open={open}
          onClose={handleClose}
          id="dialog"
          aria-labelledby="responsive-dialog-title"
          sx={isLoadingResults ? sxStyle.modalLoadPdf : sxStyle.modalPdf}
        >
          {isLoadingResults ? (
            <Grid container sx={{ p: '20px' }}>
              <CircularProgress />
            </Grid>
          ) : quotePdf ? (
            <>
              <HighlightOffIcon
                onClick={() => setOpen(false)}
                sx={sxStyle.closePdfIcon}
              />

              <Document>
                <Page
                  size="A4"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    backgroundColor: '#FFFFFF',
                  }}
                >
                  <iframe
                    src={quotePdf}
                    style={{ width: '100vw', height: '100vh' }}
                  />
                </Page>
              </Document>
            </>
          ) : (
            <div>Error al cargar el PDF</div>
          )}
        </Dialog>

        <Dialog
          onClose={handleClose}
          aria-labelledby="customized-dialog-title"
          open={openDetail}
          maxWidth={'lg'}
        >
          <MuiDialogTitle
            disableTypography
            sx={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <Typography
              variant="h6"
              sx={{
                display: 'flex',
                alignItems: 'center',
                fontWeight: 600,
                fontSize: '22px',
                color: 'black',
                marginBottom: '1%',
              }}
            >
              {'Detalle de cobertura - ' + getProductName3(element)}
            </Typography>

            <IconButton aria-label="close" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </MuiDialogTitle>
          <MuiDialogContent dividers>
            <Typography
              variant="h6"
              sx={{
                display: 'flex',
                justifyContent: 'start',
                paddingLeft: '20px',
                color: 'white',
                ...sxStyle.benefitStyle,
              }}
            >
              {'Beneficios extra:'}
            </Typography>
            <List sx={sxStyle.list}>
              <ListItem>
                <Grid container id={'listY'}>
                  <CheckIcon sx={{ color: '#28bf54' }}></CheckIcon>
                  <Typography
                    sx={{
                      textAlign: 'left',
                      fontSize: '16px',
                      marginLeft: '10px',
                    }}
                  >
                    Monto máximo global
                  </Typography>
                  <Typography
                    sx={{
                      textAlign: 'right',
                      fontSize: '16px',
                      fontWeight: '600',
                    }}
                  >
                    : {coberturaMedica(element)}
                  </Typography>
                </Grid>
              </ListItem>

              {element?.details?.assist_plans_coverages?.length > 0 &&
                element?.details?.assist_plans_coverages?.map(
                  (element, index) => {
                    if (index == 0) {
                      return;
                    }
                    return (
                      <ListItem key={index + element.description}>
                        <Grid container id={'list' + index} item>
                          <CheckIcon sx={{ color: '#28bf54' }}></CheckIcon>
                          <Typography
                            sx={{
                              textAlign: 'left',
                              fontSize: '16px',
                              marginLeft: '10px',
                            }}
                          >
                            {element.description}
                          </Typography>

                          <Typography
                            sx={{
                              textAlign: 'right',
                              fontWeight: '600',
                              fontSize: '16px',
                            }}
                          >
                            : {element.value}
                          </Typography>
                        </Grid>
                      </ListItem>
                    );
                  }
                )}
            </List>

            {element?.details?.assist_plans_includes?.length > 0 ? (
              <Typography
                variant="h6"
                sx={{
                  display: 'flex',
                  justifyContent: 'start',
                  paddingLeft: '20px',
                  color: 'white',
                  marginTop: '20px',
                  ...sxStyle.benefitStyle,
                }}
              >
                {'Incluído en tu ASSIST CARD:'}
              </Typography>
            ) : (
              ''
            )}
            <List sx={sxStyle.list}>
              {element?.details?.assist_plans_includes?.length > 0 &&
                element?.details?.assist_plans_includes?.map(
                  (element, index) => {
                    return (
                      <ListItem key={index + element.description}>
                        <CheckIcon sx={{ color: '#28bf54' }}></CheckIcon>
                        <Typography
                          sx={{
                            textAlign: 'left',
                            marginLeft: '5px',
                            fontSize: '16px',
                          }}
                        >
                          {element.description}
                        </Typography>
                        <Divider />
                      </ListItem>
                    );
                  }
                )}
            </List>
          </MuiDialogContent>
          <MuiDialogActions>
            <Button autoFocus onClick={handleClickOpen}>
              Descargar en PDF
            </Button>
            <Button autoFocus onClick={handleClose}>
              Cerrar
            </Button>
          </MuiDialogActions>
        </Dialog>
      </Grid>
    </Grid>
  );
};
